/**
 * Generates a CSS selector string from a DOM element for logging/debugging purposes.
 * Creates a complete selector path from the element to the document root.
 *
 * @param element - The DOM element to generate a selector for
 * @returns CSS selector string that identifies the element
 * @throws Error if element is null or undefined
 */
export function getElementSelector(element: Element): string {
  if (!element) {
    return 'No element provided';
  }

  if (!(element instanceof Element)) {
    throw new Error('[getElementSelector] Unexpected input, element should be an Element');
  }

  const selectorParts: string[] = [];
  let currentElement: Element | null = element;

  // Build selector path from element to root
  while (currentElement && currentElement.nodeType === Node.ELEMENT_NODE) {
    if (currentElement.tagName.toLowerCase() === 'body') {
      selectorParts.unshift('body');
      break;
    } else {
      const elementSelector = buildElementSelector(currentElement);
      selectorParts.unshift(elementSelector);
      currentElement = currentElement.parentElement;
    }
  }

  return selectorParts.join(' > ');
}

/**
 * Builds a CSS selector for a single element based on its attributes and position.
 *
 * @param element - The DOM element to build selector for
 * @returns CSS selector string for the single element
 */
function buildElementSelector(element: Element): string {
  const tagName = element.tagName.toLowerCase();
  const parts: string[] = [tagName];

  // 1. Element ID (highest priority)
  if (element.id) {
    parts.push(`#${escapeCSSIdentifier(element.id)}`);
  }

  // 2. CSS classes
  if (element.classList && element.classList.length > 0) {
    const classes = Array.from(element.classList)
      .map((cls) => `.${escapeCSSIdentifier(cls)}`)
      .join('');
    parts.push(classes);
  }

  // 3. Form element names
  if (isFormElement(element)) {
    const nameAttr = element.getAttribute('name');
    if (nameAttr) {
      parts.push(`[name="${escapeCSSAttributeValue(nameAttr)}"]`);
    }
  }

  // 4. Test identifiers
  const testAttributes = ['data-testid', 'data-cy', 'data-test'];
  for (const attr of testAttributes) {
    const value = element.getAttribute(attr);
    if (value) {
      parts.push(`[${attr}="${escapeCSSAttributeValue(value)}"]`);
    }
  }

  // 5. Structural fallback (nth-child)
  if (parts.length === 1) {
    // Only tag name, no other identifiers
    const nthChild = getNthChild(element);
    if (nthChild > 0) {
      parts.push(`:nth-child(${nthChild})`);
    }
  }

  return parts.join('');
}

/**
 * Determines if an element is a form element that can have a name attribute.
 *
 * @param element - The DOM element to check
 * @returns true if element is a form element
 */
function isFormElement(element: Element): boolean {
  return formTags.includes(element.tagName.toLowerCase());
}

/**
 * Gets the nth-child position of an element among its siblings.
 *
 * @param element - The DOM element to get position for
 * @returns The 1-based position of the element among its siblings
 */
function getNthChild(element: Element): number {
  if (!element.parentElement) {
    return 0;
  }
  const children = Array.from(element.parentElement.children);
  if (children.length === 1) {
    return 0;
  }
  return children.indexOf(element) + 1;
}

/**
 * Escapes CSS identifier characters for use in selectors.
 * Handles special characters that need escaping in CSS identifiers.
 *
 * @param identifier - The identifier to escape
 * @returns Escaped identifier safe for use in CSS selectors
 */
function escapeCSSIdentifier(identifier: string): string {
  if (window.CSS && window.CSS.escape && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(identifier);
  }
  // For older browser compatibility, use simple character replacement
  return identifier.replace(/[!"#$%&'()*+,.\/:;<=>?@\[\\\]^`{|}~]/g, '\\$&');
}

/**
 * Escapes CSS attribute values for use in selectors.
 *
 * @param value - The attribute value to escape
 * @returns Escaped value safe for use in CSS attribute selectors
 */
function escapeCSSAttributeValue(value: string): string {
  // Escape quotes and backslashes in attribute values
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

const formTags = ['input', 'select', 'textarea', 'button', 'fieldset', 'label'];
